import {
  Mesh,
  BufferGeometry,
  MeshPhongMaterial,
  Float32BufferAttribute,
  Uint16BufferAttribute,
  LineSegments,
  DepthPackingStrategies,
} from "three";

export default class MagnetGeometry extends BufferGeometry {
  radius: number;
  height: number;
  size: number;

  constructor(radius = 0.1, height = 0.3, size = 0.05, segments = 16) {
    super();
    this.radius = radius;
    this.height = height;
    this.size = size;

    const indices = [];
    const singlePoints = [];
    const vertices = [];
    const normals = [];
    const outward = [];

    let offset = 0;
    let rx = -1;
    let ry = 0;
    for (let i = 0; i <= segments; ++i) {
      if (i == 0 || i == segments) {
        offset = height;
        rx = i == 0 ? -1 : 1;
        ry = 0;
      } else {
        offset = 0;
        const angle = (Math.PI * (i - 1)) / (segments - 2);
        rx = -Math.cos(angle);
        ry = -Math.sin(angle);
      }
      outward.push(rx, ry, 0);
      for (let i = 0; i < 2; ++i)
        for (let j = 0; j < 2; ++j)
          singlePoints.push(
            rx * (radius + i * size),
            offset + ry * (radius + i * size),
            (j - 0.5) * size
          );
    }

    const addPoint = (i: number) => {
      i *= 3;
      vertices.push(singlePoints[i], singlePoints[i + 1], singlePoints[i + 2]);
    };
    const addOutwardNormal = (i: number, s = 1) => {
      i *= 3;
      normals.push(s * outward[i], s * outward[i + 1], s * outward[i + 2]);
    };

    for (let i = 0; i <= segments; ++i) {
      addPoint(4 * i);
      addPoint(4 * i + 1);
      addOutwardNormal(i, -1);
      addOutwardNormal(i, -1);

      addPoint(4 * i + 1);
      addPoint(4 * i + 3);
      normals.push(0, 0, 1, 0, 0, 1);

      addPoint(4 * i + 3);
      addPoint(4 * i + 2);
      addOutwardNormal(i, 1);
      addOutwardNormal(i, 1);

      addPoint(4 * i + 2);
      addPoint(4 * i);
      normals.push(0, 0, -1, 0, 0, -1);

      if (i > 0) {
        for (let k = 0; k < 8; k += 2) {
          indices.push(8 * i + k, 8 * i - 8 + k, 8 * i + k + 1);
          indices.push(8 * i - 8 + k, 8 * i - 8 + (k + 1), 8 * i + k + 1);
        }
      }
    }

    {
      for (let i = 0; i < 4; ++i) addPoint(i);
      for (let i = 0; i < 4; ++i) addPoint(4 * segments + i);
      for (let i = 0; i < 8; ++i) normals.push(0, 1, 0);

      let i = 8 + segments * 8;
      indices.push(i, i + 3, i + 1);
      indices.push(i, i + 2, i + 3);
      i += 4;
      indices.push(i, i + 1, i + 3);
      indices.push(i, i + 3, i + 2);
    }

    this.setIndex(new Uint16BufferAttribute(indices, 1));
    this.setAttribute("position", new Float32BufferAttribute(vertices, 3));
    this.setAttribute("normal", new Float32BufferAttribute(normals, 3));
  }

  contains([x, y, z]: [number, number, number]) {
    if (2 * Math.abs(z) > this.size) return false;
    if (y > this.height) return false;

    if (y >= 0) {
      return (
        (x > this.radius && x < this.radius + this.size) ||
        (x < -this.radius && x > -this.radius - this.size)
      );
    } else {
      const r = Math.hypot(x, y);

      return r > this.radius && r < this.radius + this.size;
    }
  }

  pointInside() {
    return [0, -this.radius - this.size / 2, 0];
  }

  magneticField([x, y, _]: [number, number, number]): [number, number, number] {
    if (y >= 0) return [0, Math.sign(x), 0];
    const r = Math.hypot(x, y);
    return [-y / r, x / r, 0];
  }
}
